import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDay, parse } from 'date-fns';
import { Form, Formik } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { DaySelector } from 'src/components/DaySelector';
import { Dialog } from 'src/components/Dialog';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Appointment } from 'src/models/appointment/_types';
import { Visit } from 'src/models/visit/_types';
import {
  createScheduledMembership,
  deleteScheduledMembership,
  fetchScheduledAppointment,
  fetchScheduledMemberships,
  updateScheduledMembership,
} from 'src/models/visit/visit';
import { FormAutocomplete } from 'src/shared/Forms/Fields/FormAutocomplete';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import api from 'src/utils/ajax';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

interface RecurringMembershipDialogProps {
  open: boolean;
  onClose: () => void;
  visit: Visit | null;
  appointment: Appointment;
  onSuccess?: () => void;
}

interface FormValues {
  recurringCount: number;
  recurringType: string;
  recurringDayNumbers: number[];
  days: number[];
  startAt: Date | null;
  endAt: Date | null;
}

const DAY_NUMBERS = [
  { id: 1, name: 'First' },
  { id: 2, name: 'Second' },
  { id: 3, name: 'Third' },
  { id: 4, name: 'Fourth' },
];

const DATES = Array.from({ length: 31 }, (_, i) => ({
  id: i + 1,
  name: `${i + 1}${getOrdinalSuffix(i + 1)}`,
}));

function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

const getRecurringTypes = (
  recurringCount: number,
  t: (key: string) => string
) => {
  const isPlural = recurringCount > 1;
  return [
    { value: 'day', label: isPlural ? t('days') : t('day') },
    { value: 'week', label: isPlural ? t('weeks') : t('week') },
    { value: 'month', label: isPlural ? t('months') : t('month') },
    { value: 'month_on_day', label: t('monthOnDaysOfWeek') },
    { value: 'month_on_date', label: t('monthOnDates') },
  ];
};

const validationSchema = Yup.object().shape({
  recurringCount: Yup.number()
    .min(1, 'Must be at least 1')
    .required('Required'),
  recurringType: Yup.string().required('Required'),
  recurringDayNumbers: Yup.array().when(['recurringType'], {
    is: (type: string) => ['month_on_date', 'month_on_day'].includes(type),
    then: () =>
      Yup.array().min(1, 'Select at least one date').required('Required'),
  }),
  days: Yup.array().when(['recurringType'], {
    is: (type: string) => ['week', 'month_on_day'].includes(type),
    then: () =>
      Yup.array().min(1, 'Select at least one day').required('Required'),
  }),
});

/**
 * Dialog for creating or editing a recurring membership for a visit.
 * Matches v2's edit-membership component functionality.
 */
export const RecurringMembershipDialog: FC<RecurringMembershipDialogProps> = ({
  open,
  onClose,
  visit,
  appointment,
  onSuccess,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.viewEventDialog.recurringMembershipDialog',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });
  const queryClient = useQueryClient();
  const scheduledAppointmentId = appointment.scheduledAppointmentId;

  // Fetch the scheduled appointment to get its recurring settings as defaults
  const { data: scheduledAppointment, isLoading: isLoadingScheduled } =
    useQuery({
      queryKey: ['scheduledAppointment', scheduledAppointmentId],
      queryFn: () => fetchScheduledAppointment(scheduledAppointmentId!),
      enabled: !!scheduledAppointmentId && open,
    });

  // Check if user already has a membership for this scheduled appointment
  const { data: existingMemberships, isLoading: isLoadingMemberships } =
    useQuery({
      queryKey: ['scheduledMemberships', visit?.userId, scheduledAppointmentId],
      queryFn: () =>
        fetchScheduledMemberships({
          userId: visit?.userId,
          scheduledAppointmentId: scheduledAppointmentId || undefined,
        }),
      enabled: !!visit?.userId && !!scheduledAppointmentId && open,
    });

  const isLoading = isLoadingScheduled || isLoadingMemberships;
  const existingMembership = existingMemberships?.data?.[0];
  const isEdit = !!existingMembership;

  // Create membership mutation
  const createMutation = useMutation({
    mutationFn: (values: FormValues) =>
      createScheduledMembership({
        userId: visit!.userId!,
        scheduledAppointmentId: scheduledAppointmentId!,
        recurringCount: values.recurringCount,
        recurringType: values.recurringType,
        recurringDayNumbers: values.recurringDayNumbers,
        days: values.days,
        startAt: values.startAt || undefined,
        endAt: values.endAt,
      }),
    onSuccess: () => {
      toast.success(t('createdSuccess'));
      queryClient.invalidateQueries({ queryKey: ['scheduledMemberships'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('createFailed')));
    },
  });

  // Update membership mutation
  const updateMutation = useMutation({
    mutationFn: (values: FormValues) =>
      updateScheduledMembership(existingMembership!.id, {
        recurringCount: values.recurringCount,
        recurringType: values.recurringType,
        recurringDayNumbers: values.recurringDayNumbers,
        days: values.days,
        startAt: values.startAt || undefined,
        endAt: values.endAt,
      }),
    onSuccess: () => {
      toast.success(t('updatedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['scheduledMemberships'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('updateFailed')));
    },
  });

  // Delete membership mutation
  const deleteMutation = useMutation({
    mutationFn: () => deleteScheduledMembership(existingMembership!.id),
    onSuccess: () => {
      toast.success(t('deletedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['scheduledMemberships'] });
      queryClient.invalidateQueries({ queryKey: ['visits'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      toast.error(api.formatError(error, t('deleteFailed')));
    },
  });

  const handleSubmit = async (values: FormValues) => {
    if (isEdit) {
      await updateMutation.mutateAsync(values);
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  if (!visit || !scheduledAppointmentId) {
    return (
      <Dialog open={open} onClose={onClose} title={t('title')}>
        <Typography color="text.secondary">{t('onlyForRecurring')}</Typography>
      </Dialog>
    );
  }

  const initialValues: FormValues = isEdit
    ? {
        recurringCount: existingMembership.recurringCount || 1,
        recurringType: existingMembership.recurringType || 'week',
        recurringDayNumbers: existingMembership.recurringDayNumbers || [],
        days: Array.isArray(existingMembership.days)
          ? typeof existingMembership.days[0] === 'number'
            ? existingMembership.days
            : existingMembership.days.map((d: any) => d.id)
          : [],
        startAt: existingMembership.startAt || null,
        endAt: existingMembership.endAt || null,
      }
    : {
        // Use scheduled appointment settings as defaults for new memberships
        recurringCount: scheduledAppointment?.recurringCount || 1,
        recurringType: scheduledAppointment?.recurringType || 'week',
        recurringDayNumbers: scheduledAppointment?.recurringDayNumbers || [],
        days: scheduledAppointment?.days || [],
        startAt: appointment.startTime,
        endAt: scheduledAppointment?.endAt
          ? typeof scheduledAppointment.endAt === 'string'
            ? parse(scheduledAppointment.endAt, 'yyyy-MM-dd', new Date())
            : scheduledAppointment.endAt
          : null,
      };

  const title = isEdit
    ? t('editTitle', { userName: visit.userName })
    : t('createTitle', { userName: visit.userName });

  return (
    <Dialog open={open} onClose={onClose} title={title} maxWidth="sm" fullWidth>
      {isLoading ? (
        <Stack spacing={2} sx={{ p: 2 }}>
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Stack>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, submitForm, isSubmitting }) => {
            const sessionDays = scheduledAppointment?.days;
            const hasSessionDayRestriction =
              sessionDays && sessionDays.length > 0;

            const shouldDisableDate = hasSessionDayRestriction
              ? (date: Date) => !sessionDays.includes(getDay(date))
              : undefined;

            return (
            <Form>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2}>
                  <FormTextField
                    name="recurringCount"
                    label={t('repeatsEvery')}
                    type="number"
                    sx={{ flex: 1 }}
                  />
                  <FormTextField
                    name="recurringType"
                    label={t('frequency')}
                    options={getRecurringTypes(values.recurringCount, t)}
                    sx={{ flex: 2 }}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (
                        value === 'month_on_day' ||
                        value === 'month_on_date'
                      ) {
                        setFieldValue('recurringDayNumbers', [1]);
                      } else {
                        setFieldValue('recurringDayNumbers', []);
                      }
                    }}
                  />
                </Stack>

                {values.recurringType === 'week' && (
                  <DaySelector
                    label={t('daysOfWeek')}
                    value={values.days}
                    onChange={(days) => setFieldValue('days', days)}
                    allowedDays={sessionDays}
                  />
                )}

                {values.recurringType === 'month_on_date' && (
                  <FormAutocomplete
                    name="recurringDayNumbers"
                    label={t('dates')}
                    options={DATES}
                    multiple
                    getOptionLabel={(option: any) => {
                      const found = DATES.find(
                        (d) =>
                          d.id ===
                          (typeof option === 'number' ? option : option.id)
                      );
                      return found?.name || '';
                    }}
                    isOptionEqualToValue={(option: any, value: any) => {
                      const optionId =
                        typeof option === 'number' ? option : option.id;
                      const valueId =
                        typeof value === 'number' ? value : value.id;
                      return optionId === valueId;
                    }}
                    transform={(value: any) =>
                      value.map((v: any) => (typeof v === 'number' ? v : v.id))
                    }
                    placeholder={t('selectDates')}
                    limitTags={2}
                  />
                )}

                {values.recurringType === 'month_on_day' && (
                  <Stack spacing={2}>
                    <FormAutocomplete
                      name="recurringDayNumbers"
                      label={t('whichOccurrence')}
                      options={DAY_NUMBERS}
                      multiple
                      getOptionLabel={(option: any) => {
                        if (typeof option === 'number') {
                          const found = DAY_NUMBERS.find(
                            (d) => d.id === option
                          );
                          return found ? found.name : '';
                        }
                        return option?.name || '';
                      }}
                      isOptionEqualToValue={(option: any, value: any) => {
                        const optionId =
                          typeof option === 'number' ? option : option.id;
                        const valueId =
                          typeof value === 'number' ? value : value.id;
                        return optionId === valueId;
                      }}
                      placeholder={t('anyDates')}
                      limitTags={2}
                    />
                    <DaySelector
                      label={t('daysOfWeek')}
                      value={values.days}
                      onChange={(days) => setFieldValue('days', days)}
                      allowedDays={sessionDays}
                    />
                  </Stack>
                )}

                <Stack direction="row" spacing={2}>
                  <FormDatePicker
                    name="startAt"
                    label={t('startDate')}
                    shouldDisableDate={shouldDisableDate}
                  />
                  <FormDatePicker
                    name="endAt"
                    label={t('endDateOptional')}
                    minDate={values.startAt || undefined}
                    shouldDisableDate={shouldDisableDate}
                    clearable
                  />
                </Stack>

                {/* Action buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 2,
                  }}
                >
                  {isEdit ? (
                    <Button
                      color="error"
                      variant="borderless"
                      onClick={() => deleteMutation.mutate()}
                      disabled={deleteMutation.isPending}
                    >
                      {t('deleteMembership')}
                    </Button>
                  ) : (
                    <Box />
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="bordered"
                      color="neutral"
                      onClick={onClose}
                    >
                      {tCommon('cancel')}
                    </Button>
                    <Button
                      variant="prominent"
                      color="primary"
                      onClick={submitForm}
                      disabled={
                        isSubmitting ||
                        createMutation.isPending ||
                        updateMutation.isPending
                      }
                    >
                      {isEdit ? t('updateMembership') : t('createMembership')}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            </Form>
            );
          }}
        </Formik>
      )}
    </Dialog>
  );
};
